import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, ShoppingCart, Users, Plus, Menu, Package, Wallet, Truck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Bottom navigation para móvil — patrón familiar (tipo Instagram/Notion).
 * 5 items: 2 links → botón central "+" elevado → 2 links.
 * El botón "+" abre el drawer si estás en dashboard, o navega a Pedidos con
 * flag ?nuevo para abrir el modal directo.
 *
 * Perms:
 *  - FAB "+" solo si tiene `ventas.crear`.
 *  - Clientes solo si puede verlos o gestionarlos.
 *  - Se adapta al rol operativo (Despachos en vez de Clientes cuando aplica).
 */
export default function BottomNav({ isAdmin, onOpenDrawer, hidden = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const bypass = isAdmin || perms.includes("permisos.administrar");
  const has = (p) => bypass || perms.includes(p);
  const canCreatePedido = has("ventas.crear");
  const canVerClientes = has("clientes.crear") || has("clientes.editar") || has("ventas.crear") || has("ventas.ver_todos");
  const canDespachar = has("despachos.tomar") || has("despachos.gestionar");
  const canVerPagos = has("pagos.reportar") || has("pagos.aprobar");

  // Items adaptados al rol y permisos.
  const items = isAdmin
    ? [
        { to: "/", label: "Panel", icon: LayoutDashboard, testid: "bnav-panel" },
        { to: "/pedidos", label: "Pedidos", icon: ShoppingCart, testid: "bnav-pedidos" },
        // slot central del "+"
        { to: "/clientes", label: "Clientes", icon: Users, testid: "bnav-clientes" },
        { to: "/pagos", label: "Pagos", icon: Wallet, testid: "bnav-pagos" },
      ]
    : (() => {
        const left = [
          { to: "/", label: "Mi día", icon: LayoutDashboard, testid: "bnav-panel" },
          { to: "/pedidos", label: "Pedidos", icon: ShoppingCart, testid: "bnav-pedidos" },
        ];
        const right = [];
        if (canVerClientes) right.push({ to: "/clientes", label: "Clientes", icon: Users, testid: "bnav-clientes" });
        else if (canDespachar) right.push({ to: "/pedidos?estado=confirmado", label: "Despachos", icon: Truck, testid: "bnav-despachos" });
        else right.push({ to: "/productos", label: "Productos", icon: Package, testid: "bnav-productos" });
        if (canVerPagos) right.push({ to: "/pagos", label: "Pagos", icon: Wallet, testid: "bnav-pagos" });
        else right.push({ to: "/productos", label: "Productos", icon: Package, testid: "bnav-productos-2" });
        return [...left, ...right];
      })();

  const onFabClick = () => {
    // En móvil (viewport pequeño), abrir el nuevo "Modo Vendedor" rápido.
    // En desktop/tablet amplio no usamos este nav, pero por seguridad fallback.
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      navigate("/pedidos/movil");
      return;
    }
    // Vamos a /pedidos con query ?nuevo=1 (la página abre el wizard si lo detecta).
    // Si ya estamos en /pedidos, forzamos un cambio para disparar la lógica.
    if (location.pathname === "/pedidos") {
      navigate("/pedidos?nuevo=1&t=" + Date.now(), { replace: true });
    } else {
      navigate("/pedidos?nuevo=1");
    }
  };

  return (
    <nav
      className={`md:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur-xl border-t border-border/60 pb-safe ${hidden ? "hidden" : ""}`}
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 4px)" }}
      data-testid="bottom-nav"
    >
      <div className="relative grid grid-cols-5 items-center h-16">
        {items.slice(0, 2).map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to} to={to} end={to === "/"} data-testid={testid}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 h-full ${
                isActive ? "text-primary" : "text-foreground/60"
              }`
            }
          >
            <Icon className="w-[19px] h-[19px]" strokeWidth={1.9} />
            <span className="text-[9px] uppercase tracking-wider font-semibold">{label}</span>
          </NavLink>
        ))}

        {/* Botón central "+" elevado — solo visible si el usuario puede crear pedidos */}
        <div className="flex items-center justify-center">
          {canCreatePedido ? (
            <button
              onClick={onFabClick}
              className="relative -translate-y-3 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-soft-lift hover:scale-105 active:scale-95 transition-transform flex items-center justify-center ring-4 ring-background"
              data-testid="bnav-nuevo"
              aria-label="Nuevo pedido"
            >
              <Plus className="w-6 h-6" strokeWidth={2.4} />
            </button>
          ) : (
            <button
              onClick={onOpenDrawer}
              className="relative -translate-y-3 w-14 h-14 rounded-full bg-card border border-border text-foreground/70 shadow-sm hover:scale-105 transition-transform flex items-center justify-center ring-4 ring-background"
              data-testid="bnav-menu"
              aria-label="Abrir menú"
            >
              <Menu className="w-6 h-6" strokeWidth={2.2} />
            </button>
          )}
        </div>

        {items.slice(2, 4).map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to} to={to} end={to === "/"} data-testid={testid}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 h-full ${
                isActive ? "text-primary" : "text-foreground/60"
              }`
            }
          >
            <Icon className="w-[19px] h-[19px]" strokeWidth={1.9} />
            <span className="text-[9px] uppercase tracking-wider font-semibold">{label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
